import React, { useState, useEffect } from "react";
import FilterBuilder from "./FilterBuilder";
import GroupingBuilder from "./GroupingBuilder";
import AggregationBuilder from "./AggregationBuilder";
import ReportPreview from "./ReportPreview";
import { parseTimeline, calculateStageDurations, getTotalDuration } from "../utils/timelineParser";

const ReportTemplateBuilder = ({ data, fieldMetadata, template, onSave, onCancel, refreshData }) => {
  console.log('ReportTemplateBuilder - props:', {
    dataLength: data?.length,
    fieldMetadataKeys: Object.keys(fieldMetadata || {}).length,
    fieldMetadata: fieldMetadata,
    template: template
  });

  const [templateConfig, setTemplateConfig] = useState({
    name: '',
    description: '',
    dateRange: {
      from: '',
      to: ''
    },
    filters: [],
    grouping: [],
    aggregations: [],
    sorting: {
      field: '',
      direction: 'asc'
    }
  });

  const [activeStep, setActiveStep] = useState(1);
  const [previewData, setPreviewData] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [currentDataPeriod, setCurrentDataPeriod] = useState(null);

  // Инициализация шаблона при редактировании
  useEffect(() => {
    if (template) {
      setTemplateConfig(template);
    }
  }, [template]);

  // Обновление превью при изменении конфигурации
  useEffect(() => {
    generatePreview();
  }, [templateConfig, data]);

  const generatePreview = () => {
    // Проверяем, нужно ли загрузить данные для конкретного периода
    const templateDateRange = templateConfig.dateRange;
    const hasDateRange = templateDateRange && (templateDateRange.from || templateDateRange.to);

    if (hasDateRange && refreshData) {
      const periodKey = `${templateDateRange.from || ''}-${templateDateRange.to || ''}`;

      // Проверяем, совпадает ли текущий период данных с требуемым
      if (currentDataPeriod !== periodKey) {
        console.log('Нужно загрузить данные для превью за период:', templateDateRange);
        loadDataForPreview(templateDateRange, periodKey);
        return;
      }
    }

    // Обычная обработка данных без загрузки
    processPreviewData(data);
  };

  const loadDataForPreview = async (dateRange, periodKey) => {
    setPreviewLoading(true);
    try {
      console.log('Загружаем данные для превью за период:', dateRange);
      await refreshData(dateRange);
      setCurrentDataPeriod(periodKey);

      // После загрузки данных обрабатываем их
      // Используем setTimeout чтобы дать время data обновиться
      setTimeout(() => {
        processPreviewData(data);
        setPreviewLoading(false);
      }, 100);
    } catch (error) {
      console.error('Ошибка загрузки данных для превью:', error);
      setPreviewLoading(false);
    }
  };

  const processPreviewData = (sourceData) => {
    if (!sourceData || sourceData.length === 0) {
      setPreviewData([]);
      return;
    }

    // Обогащаем данные временными метриками из таймлайна
    let filteredData = sourceData.map(item => {
      const timelineFieldId = 'ufCrm3_1757404689894';
      const timelineText = item[timelineFieldId];

      if (!timelineText) {
        return item;
      }

      // Парсим таймлайн и вычисляем длительности
      const events = parseTimeline(timelineText);
      if (events.length === 0) {
        return item;
      }

      const stageDurations = calculateStageDurations(events);
      const totalDuration = getTotalDuration(timelineText);

      // Добавляем временные метрики для каждой стадии
      const enrichedItem = { ...item };

      // Общее время
      if (totalDuration) {
        enrichedItem._totalProductionTime = parseFloat(totalDuration.durationHours);
      }

      // Время на каждой стадии
      stageDurations.forEach(duration => {
        const stageName = duration.stage;
        const fieldName = `_time_${stageName.toLowerCase().replace(/\s+/g, '_')}`;
        enrichedItem[fieldName] = duration.durationHours;
      });

      // Также добавляем поля для агрегаций (например, timeline_avg_stage_резка_стекла)
      // Для каждой агрегации типа timeline_*_stage добавляем индивидуальное время
      templateConfig.aggregations?.forEach(agg => {
        if (agg.type && agg.type.includes('_stage') && agg.stageName) {
          const stageDuration = stageDurations.find(d =>
            d.stage.toLowerCase().includes(agg.stageName.toLowerCase())
          );
          if (stageDuration && agg.outputField) {
            enrichedItem[agg.outputField] = stageDuration.durationHours;
          }
        } else if (agg.type && agg.type.includes('_total')) {
          // Для общего времени
          if (totalDuration && agg.outputField) {
            enrichedItem[agg.outputField] = parseFloat(totalDuration.durationHours);
          }
        }
      });

      return enrichedItem;
    });

    // Применяем фильтры
    templateConfig.filters.forEach(filter => {
      filteredData = applyFilter(filteredData, filter);
    });

    // Применяем группировку
    if (templateConfig.grouping.length > 0) {
      filteredData = applyGrouping(filteredData, templateConfig.grouping);
    }

    // Применяем агрегацию
    if (templateConfig.aggregations.length > 0) {
      filteredData = applyAggregations(filteredData, templateConfig.aggregations);
    }

    // Применяем сортировку
    if (templateConfig.sorting.field) {
      filteredData = applySorting(filteredData, templateConfig.sorting);
    }

    setPreviewData(filteredData);
  };

  const applyFilter = (data, filter) => {
    return data.filter(item => {
      const value = getNestedValue(item, filter.field);

      switch (filter.operator) {
        case 'equals':
          return value === filter.value;
        case 'contains':
          return String(value).toLowerCase().includes(String(filter.value).toLowerCase());
        case 'startsWith':
          return String(value).toLowerCase().startsWith(String(filter.value).toLowerCase());
        case 'endsWith':
          return String(value).toLowerCase().endsWith(String(filter.value).toLowerCase());
        case 'substring':
          const startIndex = parseInt(filter.startIndex) || 0;
          const length = parseInt(filter.length) || String(value).length;
          const substring = String(value).substring(startIndex, startIndex + length);
          return substring === filter.value;
        case 'greaterThan':
          return parseFloat(value) > parseFloat(filter.value);
        case 'lessThan':
          return parseFloat(value) < parseFloat(filter.value);
        case 'between':
          const num = parseFloat(value);
          return num >= parseFloat(filter.minValue) && num <= parseFloat(filter.maxValue);
        case 'isEmpty':
          return !value || value === '';
        case 'isNotEmpty':
          return value && value !== '';
        default:
          return true;
      }
    });
  };

  const applyGrouping = (data, groupingConfig) => {
    const grouped = {};

    data.forEach(item => {
      let groupKey = '';

      groupingConfig.forEach(group => {
        const value = getNestedValue(item, group.field);
        let groupValue = value;

        if (group.type === 'substring') {
          const startIndex = parseInt(group.startIndex) || 0;
          const length = parseInt(group.length) || String(value).length;
          groupValue = String(value).substring(startIndex, startIndex + length);
        } else if (group.type === 'range') {
          const num = parseFloat(value);
          const ranges = group.ranges || [];
          const matchedRange = ranges.find(range =>
            num >= parseFloat(range.min) && num <= parseFloat(range.max)
          );
          groupValue = matchedRange ? matchedRange.label : 'Не определено';
        }

        groupKey += (groupKey ? ' | ' : '') + `${group.field}: ${groupValue}`;
      });

      if (!grouped[groupKey]) {
        grouped[groupKey] = {
          groupKey,
          items: [],
          count: 0
        };
      }

      grouped[groupKey].items.push(item);
      grouped[groupKey].count++;
    });

    return Object.values(grouped);
  };

  const applyAggregations = (data, aggregations) => {
    return data.map(group => {
      const aggregatedData = { ...group };

      aggregations.forEach(agg => {
        const values = group.items ? group.items.map(item => getNestedValue(item, agg.field)) : [getNestedValue(group, agg.field)];
        const numericValues = values.map(v => parseFloat(v)).filter(v => !isNaN(v));

        switch (agg.type) {
          case 'count':
            aggregatedData[agg.outputField || `${agg.field}_count`] = group.items ? group.items.length : 1;
            break;
          case 'sum':
            aggregatedData[agg.outputField || `${agg.field}_sum`] = numericValues.reduce((sum, val) => sum + val, 0);
            break;
          case 'avg':
            aggregatedData[agg.outputField || `${agg.field}_avg`] = numericValues.length > 0 ?
              numericValues.reduce((sum, val) => sum + val, 0) / numericValues.length : 0;
            break;
          case 'min':
            aggregatedData[agg.outputField || `${agg.field}_min`] = numericValues.length > 0 ? Math.min(...numericValues) : 0;
            break;
          case 'max':
            aggregatedData[agg.outputField || `${agg.field}_max`] = numericValues.length > 0 ? Math.max(...numericValues) : 0;
            break;
        }
      });

      return aggregatedData;
    });
  };

  const applySorting = (data, sorting) => {
    return [...data].sort((a, b) => {
      const aVal = getNestedValue(a, sorting.field);
      const bVal = getNestedValue(b, sorting.field);

      if (aVal < bVal) return sorting.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sorting.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  const handleSave = () => {
    if (!templateConfig.name) {
      alert('Пожалуйста, укажите название шаблона');
      return;
    }

    onSave({
      ...templateConfig,
      updatedAt: new Date().toISOString()
    });
  };

  const steps = [
    { id: 1, name: 'Основные настройки', icon: '⚙️' },
    { id: 2, name: 'Фильтры', icon: '🔍' },
    { id: 3, name: 'Группировка', icon: '📊' },
    { id: 4, name: 'Счетчики', icon: '🔢' },
    { id: 5, name: 'Превью', icon: '👁️' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">

        {/* Заголовок */}
        <div className="px-6 py-4 border-b bg-gray-50">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              {template ? 'Редактирование шаблона' : 'Создание шаблона отчета'}
            </h2>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Навигация по шагам */}
        <div className="px-6 py-3 border-b bg-gray-50">
          <div className="flex space-x-4">
            {steps.map((step) => (
              <button
                key={step.id}
                onClick={() => setActiveStep(step.id)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeStep === step.id
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <span>{step.icon}</span>
                <span>{step.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Содержимое шагов */}
        <div className="px-6 py-6 max-h-[60vh] overflow-y-auto">
          {activeStep === 1 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold">Основные настройки</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Название шаблона *
                  </label>
                  <input
                    type="text"
                    value={templateConfig.name}
                    onChange={(e) => setTemplateConfig({...templateConfig, name: e.target.value})}
                    placeholder="Например: Продажи по франчайзи"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Описание
                  </label>
                  <input
                    type="text"
                    value={templateConfig.description}
                    onChange={(e) => setTemplateConfig({...templateConfig, description: e.target.value})}
                    placeholder="Краткое описание шаблона"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Период данных
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  Указанный период будет использован для загрузки данных с сервера при применении шаблона.
                  Если период не указан, будут использованы данные за последний месяц.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Дата начала</label>
                    <input
                      type="date"
                      value={templateConfig.dateRange.from}
                      onChange={(e) => setTemplateConfig({
                        ...templateConfig,
                        dateRange: {...templateConfig.dateRange, from: e.target.value}
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Дата окончания</label>
                    <input
                      type="date"
                      value={templateConfig.dateRange.to}
                      onChange={(e) => setTemplateConfig({
                        ...templateConfig,
                        dateRange: {...templateConfig.dateRange, to: e.target.value}
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                {(templateConfig.dateRange.from || templateConfig.dateRange.to) && (
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-sm text-blue-700">
                    🔄 При применении этого шаблона данные будут загружены с сервера за указанный период
                  </div>
                )}
              </div>

              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={templateConfig.isShared || false}
                    onChange={(e) => setTemplateConfig({...templateConfig, isShared: e.target.checked})}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Сделать шаблон общим</span>
                </label>
                <p className="text-xs text-gray-500 mt-1 ml-6">
                  Общие шаблоны доступны всем пользователям системы
                </p>
              </div>
            </div>
          )}

          {activeStep === 2 && (
            <FilterBuilder
              fieldMetadata={fieldMetadata}
              filters={templateConfig.filters}
              onChange={(filters) => setTemplateConfig({...templateConfig, filters})}
            />
          )}

          {activeStep === 3 && (
            <GroupingBuilder
              fieldMetadata={fieldMetadata}
              grouping={templateConfig.grouping}
              onChange={(grouping) => setTemplateConfig({...templateConfig, grouping})}
            />
          )}

          {activeStep === 4 && (
            <AggregationBuilder
              fieldMetadata={fieldMetadata}
              aggregations={templateConfig.aggregations}
              onChange={(aggregations) => setTemplateConfig({...templateConfig, aggregations})}
            />
          )}

          {activeStep === 5 && (
            <div>
              {previewLoading && (
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-3"></div>
                    <span className="text-blue-700">Загружаем данные за указанный период для превью...</span>
                  </div>
                </div>
              )}

              {templateConfig.dateRange && (templateConfig.dateRange.from || templateConfig.dateRange.to) && (
                <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-gray-600">
                        📅 Превью показывает данные за период: {templateConfig.dateRange.from || 'начало'} — {templateConfig.dateRange.to || 'текущая дата'}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Всего записей в превью: {previewData.length}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        const periodKey = `${templateConfig.dateRange.from || ''}-${templateConfig.dateRange.to || ''}`;
                        setCurrentDataPeriod(null); // Сбрасываем кеш
                        loadDataForPreview(templateConfig.dateRange, periodKey);
                      }}
                      disabled={previewLoading}
                      className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
                    >
                      🔄 Обновить данные
                    </button>
                  </div>
                </div>
              )}

              <ReportPreview
                data={previewData}
                templateConfig={templateConfig}
                fieldMetadata={fieldMetadata}
              />
            </div>
          )}
        </div>

        {/* Футер с кнопками */}
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-between">
          <div className="flex space-x-3">
            {activeStep > 1 && (
              <button
                onClick={() => setActiveStep(activeStep - 1)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
              >
                ← Назад
              </button>
            )}
            {activeStep < steps.length && (
              <button
                onClick={() => setActiveStep(activeStep + 1)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Далее →
              </button>
            )}
          </div>

          <div className="flex space-x-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              Отмена
            </button>
            <button
              onClick={handleSave}
              className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors font-medium"
            >
              Сохранить шаблон
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportTemplateBuilder;